
import { Agency, Owner } from '../types';

// API Configuration
const YOUSIGN_API_URL_PROD = 'https://api.yousign.app/v3';
const YOUSIGN_API_URL_SANDBOX = 'https://api-sandbox.yousign.app/v3';

const getBaseUrl = (env?: 'sandbox' | 'live') => {
    return env === 'live' ? YOUSIGN_API_URL_PROD : YOUSIGN_API_URL_SANDBOX;
};

// Types pour les infos signataire
export interface SignerInfo {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
}

export const yousignService = {
    
    // 1. Initialiser une demande de signature
    initiateSignatureRequest: async (apiKey: string, env: 'sandbox' | 'live', name: string, agency?: Agency) => {
        const baseUrl = getBaseUrl(env);
        
        // Construction des textes dynamiques
        const city = agency?.city ? ` ${agency.city}` : '';
        const agencyName = `Placid${city}`;
        
        // Signature d'email avec coordonnées
        const footer = agency ? 
            `\n\nCordialement,\nL'équipe ${agencyName}\n${agency.phone || ''}\n${agency.email || ''}` : 
            `\n\nCordialement,\nL'équipe Placid`;

        const requestSubject = `${agencyName} - Signature électronique de votre contrat de gestion en conciergerie`;
        const requestBody = `Bonjour,\n\nVotre contrat de gestion avec ${agencyName} est prêt.\nMerci de bien vouloir le consulter et le signer électroniquement en cliquant sur le bouton ci-dessous.${footer}`;

        const reminderSubject = `Rappel : ${agencyName} - Signature de votre contrat de gestion`;
        const reminderBody = `Bonjour,\n\nCeci est un rappel concernant votre contrat de gestion ${agencyName} en attente de signature.\nMerci de finaliser la procédure dès que possible.${footer}`;

        const response = await fetch(`${baseUrl}/signature_requests`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${apiKey}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                name: name,
                delivery_mode: 'email',
                timezone: 'Europe/Paris',
                ordered_signers: false, // Parallèle : les deux signataires peuvent signer en même temps
                email_notification: {
                    sender: {  
                      type: "custom",
                      custom_name: agencyName
                    },
                    custom_text: {
                        request_subject: requestSubject,
                        request_body: requestBody,
                        reminder_subject: reminderSubject,
                        reminder_body: reminderBody
                    }
                }
            })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(`Yousign Init Error (${env}): ${JSON.stringify(error)}`);
        }

        return await response.json();
    },

    // 2. Uploader le document PDF
    uploadDocument: async (apiKey: string, env: 'sandbox' | 'live', signatureRequestId: string, pdfBlob: Blob, fileName: string) => {
        const baseUrl = getBaseUrl(env);
        const formData = new FormData();
        formData.append('file', pdfBlob, fileName);
        formData.append('nature', 'signable_document');
        
        // Configuration des initiales : en bas à gauche de chaque page
        // Yousign positionne automatiquement les initiales selon l'alignment
        // Les paramètres doivent être envoyés comme champs séparés dans FormData
        formData.append('initials[alignment]', 'bottom-left');
        formData.append('initials[y]', '10');

        const response = await fetch(`${baseUrl}/signature_requests/${signatureRequestId}/documents`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${apiKey}`
                // Note: Ne pas mettre Content-Type pour FormData
            },
            body: formData
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(`Yousign Upload Error: ${JSON.stringify(error)}`);
        }

        return await response.json();
    },

    // 3. Ajouter un signataire (Générique)
    // Création explicite des champs de signature avec coordonnées fixes
    // Note: Les initiales sont configurées lors de l'upload du document, pas ici
    addSigner: async (
        apiKey: string, 
        env: 'sandbox' | 'live', 
        signatureRequestId: string, 
        documentId: string, 
        signerInfo: SignerInfo, 
        role: 'agency' | 'client',
        totalPages: number
    ) => {
        const baseUrl = getBaseUrl(env);
        const fields = [];
        
        // Coordonnées des signatures (en pixels, selon spécifications)
        const signatureCoords = role === 'agency' 
            ? { page: 4, width: 194, height: 49, x: 73, y: 707 }
            : { page: 4, width: 194, height: 49, x: 326, y: 706 };

        // Génération de la Signature (Page 4, coordonnées spécifiques)
        fields.push({
            document_id: documentId,
            type: 'signature',
            page: signatureCoords.page,
            x: signatureCoords.x,
            y: signatureCoords.y,
            width: signatureCoords.width,
            height: signatureCoords.height
        });

        const response = await fetch(`${baseUrl}/signature_requests/${signatureRequestId}/signers`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${apiKey}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                info: {
                    first_name: signerInfo.firstName,
                    last_name: signerInfo.lastName,
                    email: signerInfo.email,
                    phone_number: signerInfo.phone, // Format E.164 requis (+33...)
                    locale: 'fr'
                },
                fields: fields,
                signature_level: 'electronic_signature',
                signature_authentication_mode: 'no_otp'
            })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(`Yousign Signer Error (${role}): ${JSON.stringify(error)}`);
        }

        return await response.json();
    },

    // 4. Activer la demande (Envoi de l'email)
    activateRequest: async (apiKey: string, env: 'sandbox' | 'live', signatureRequestId: string) => {
        const baseUrl = getBaseUrl(env);
        const response = await fetch(`${baseUrl}/signature_requests/${signatureRequestId}/activate`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${apiKey}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(`Yousign Activate Error: ${JSON.stringify(error)}`);
        }

        return await response.json();
    },

    // 5. Vérifier le statut
    getSignatureRequestStatus: async (apiKey: string, env: 'sandbox' | 'live', signatureRequestId: string) => {
        const baseUrl = getBaseUrl(env);
        const response = await fetch(`${baseUrl}/signature_requests/${signatureRequestId}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${apiKey}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error('Impossible de récupérer le statut Yousign');
        }

        return await response.json();
    },

    // 6. Télécharger le document signé
    downloadSignedDocument: async (apiKey: string, env: 'sandbox' | 'live', signatureRequestId: string): Promise<Blob> => {
        const baseUrl = getBaseUrl(env);
        
        // Etape 1: Lister les documents pour trouver l'ID du document signé
        const listResponse = await fetch(`${baseUrl}/signature_requests/${signatureRequestId}/documents`, {
            headers: { 'Authorization': `Bearer ${apiKey}` }
        });
        
        if (!listResponse.ok) throw new Error("Impossible de lister les documents Yousign");
        const documents = await listResponse.json();
        
        // On cherche le document de nature 'signable_document'
        const signedDoc = documents.find((d: any) => d.nature === 'signable_document');
        if (!signedDoc) throw new Error("Document signé introuvable");

        // Etape 2: Télécharger le binaire
        const downloadResponse = await fetch(`${baseUrl}/signature_requests/${signatureRequestId}/documents/${signedDoc.id}/download`, {
            headers: { 'Authorization': `Bearer ${apiKey}` }
        });

        if (!downloadResponse.ok) throw new Error("Erreur téléchargement PDF Yousign");
        
        return await downloadResponse.blob();
    },

    // 7. Relancer un signataire (NEW)
    sendReminder: async (apiKey: string, env: 'sandbox' | 'live', signatureRequestId: string, signerId: string) => {
        const baseUrl = getBaseUrl(env);
        const response = await fetch(`${baseUrl}/signature_requests/${signatureRequestId}/signers/${signerId}/send_reminder`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${apiKey}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            // Check if error is because it's too soon (often happens)
            const error = await response.json();
            throw new Error(`Erreur relance: ${error.detail || 'Impossible de relancer pour le moment'}`);
        }

        return true;
    },

    // 8. Récupérer l'audit trail d'un signataire (pour obtenir la date de signature précise)
    getSignerAuditTrail: async (apiKey: string, env: 'sandbox' | 'live', signatureRequestId: string, signerId: string) => {
        const baseUrl = getBaseUrl(env);
        const response = await fetch(`${baseUrl}/signature_requests/${signatureRequestId}/signers/${signerId}/audit_trails`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${apiKey}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            // If audit trail is not available yet, return null instead of throwing
            if (response.status === 404) {
                return null;
            }
            throw new Error('Impossible de récupérer l\'audit trail du signataire');
        }

        return await response.json();
    }
};
